/**
 * The page bar — plan §3.4.3. Everything page-specific lives here: breadcrumbs,
 * title, actions, tabs. The primary header above it never carries any of them.
 *
 * Three variants are drawn in `header-page-bar.png` — list, detail and deep
 * page — and they are the same component with different props.
 */
import { useState, type ReactNode } from 'react'
import { TabRemove } from '../components/overlays.js'
import { ContextMenu, type MenuItem } from '../components/context-menu.js'
import { IconChevronLeft, IconPin } from './icons.js'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'

export interface Breadcrumb {
  label: string
  onClick?: () => void
}

export interface PageTab {
  key: string
  label: string
  /** Rendered as "Needs invoicing · 12" (mockup). */
  count?: number
  /**
   * Present on tabs that stand for something a person made — a saved view, a
   * quote option. Built-in tabs have no ×, because there is nothing to remove.
   */
  onRemove?: () => void
  /**
   * Whether this is the view the person lands on.
   *
   * The pin is a *marker*, not a control: it appears only on the view you land
   * on. Making a view the default is a right-click, because a second clickable
   * glyph inside the tab pushed the × so far from the name that removing a view
   * looked like it belonged to the tab beside it.
   */
  isDefault?: boolean
  onToggleDefault?: () => void
  /** Write the current view over the saved one this tab stands for. */
  onUpdate?: () => void
  /** Give the saved view this tab stands for a different name. */
  onRename?: () => void
}

export interface PageBarProps {
  title: string
  /** "150 of 1,240 · Harrisburg district" — visible count within scope. */
  sub?: string
  breadcrumbs?: Breadcrumb[]
  /** Status beside the title, e.g. "Draft v2 · saved 12s ago". */
  status?: ReactNode
  actions?: ReactNode
  tabs?: PageTab[]
  activeTab?: string
  onTabChange?: (key: string) => void
  /** The "+ Save view" affordance at the end of the saved-view tabs. */
  onSaveView?: () => void
  /**
   * Which of the two page bars this is (`design-system.md` §2).
   *
   * A **page** bar — settings, record, quote, schedule — titles at 18px with a
   * faint sub, because the title is the name of the thing you opened. A
   * **list** bar stays at 16px, where the tabs and the count carry the weight
   * and an 18px "Contacts" would shout. The design system says list bars stay
   * 16px "until touched", so `list` is the default and a screen opts in.
   */
  variant?: 'list' | 'page'
}

export function PageBar({
  title, sub, breadcrumbs, status, actions, tabs, activeTab, onTabChange, onSaveView,
  variant = 'list',
}: PageBarProps) {
  const [menu, setMenu] = useState<{ items: MenuItem[]; x: number; y: number } | null>(null)

  /** What a right-click on a tab offers. Empty means the tab has no menu. */
  const tabMenu = (tab: PageTab): MenuItem[] => {
    const items: MenuItem[] = []
    if (tab.onUpdate) {
      items.push({
        key: 'update', label: 'Update', tone: 'primary', onSelect: () => tab.onUpdate?.(),
      })
    }
    if (tab.onRename) {
      items.push({ key: 'rename', label: 'Rename', onSelect: () => tab.onRename?.() })
    }
    if (tab.onToggleDefault) {
      items.push({
        key: 'default',
        label: tab.isDefault ? 'Remove default' : 'Make default',
        separatorBefore: items.length > 0,
        onSelect: () => tab.onToggleDefault?.(),
      })
    }
    if (tab.onRemove) {
      items.push({
        key: 'remove', label: `Remove ${tab.label}`, tone: 'danger', separatorBefore: items.length > 0,
        onSelect: () => tab.onRemove?.(),
      })
    }
    return items
  }

  /*
   * A record page uses this bar for its breadcrumb alone — no title, no tabs.
   * The bottom padding normally comes from the tab row's own `py-2`, so
   * without tabs the strip had 10px above the words and nothing below them,
   * and the breadcrumb sat high in its own box.
   */
  const phone = usePhone()
  const hasTabs = Boolean(tabs && tabs.length > 0)

  /*
   * One tab row, drawn under whichever header this is. It scrolls sideways
   * when the saved views run past the edge.
   *
   * **`no-scrollbar`**, which is the same class `PhoneFilterStrip` and the
   * date picker's rail already carry (Aaron). On a phone the bar does not
   * overlay: it takes a row of its own under the tabs, so a list with four
   * saved views gave up a strip of height to a control nobody drags — and it
   * only fades once you have scrolled *and let go*, so it sits there on a
   * page somebody has only read. The tabs still scroll; anybody who has seen
   * one run off the edge knows to push it.
   */
  const tabRow = hasTabs
    ? (
        <div role="tablist" className="no-scrollbar flex gap-[18px] overflow-x-auto">
          {(tabs ?? []).map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={tab.key === activeTab}
              onClick={() => onTabChange?.(tab.key)}
              onContextMenu={tabMenu(tab).length === 0 ? undefined : (event) => {
                event.preventDefault()
                setMenu({ items: tabMenu(tab), x: event.clientX, y: event.clientY })
              }}
              className={cn(
                /*
                 * A flex row, not a line of inline spans. The pin used to be
                 * inline-block nudged down a pixel, which is a guess that only
                 * looks right at one font size; `items-center` is the same
                 * answer at every size.
                 */
                'group/tab flex items-center gap-1 whitespace-nowrap border-b-2 px-0.5 py-2 text-cell',
                tab.key === activeTab
                  ? 'border-text font-semibold text-text'
                  : 'border-transparent font-normal text-text-muted hover:text-text',
              )}
            >
              {tab.isDefault && (
                <span
                  aria-label="Your default view"
                  title="Your default view"
                  className="flex shrink-0 text-accent"
                >
                  <IconPin size={12} />
                </span>
              )}
              {tab.label}
              {tab.count !== undefined && <span className="tnum">· {tab.count}</span>}
              {tab.onRemove && <TabRemove label={tab.label} onRemove={tab.onRemove} />}
            </button>
          ))}

          {onSaveView && (
            <button
              type="button"
              onClick={onSaveView}
              className="whitespace-nowrap border-b-2 border-transparent px-0.5 py-2 text-cell text-text-muted hover:text-text"
            >
              + Save view
            </button>
          )}
        </div>
    )
    : null

  /*
   * A phone gets the header `org-12-phones.html` draws, which is the header
   * every phone operating system draws: a back chevron, the name of the thing
   * on one line, what it is under that, and one action.
   *
   * The desktop row is crumbs, title, sub and actions side by side, and at
   * 390px that is four things competing for 358px — the sub wrapped to three
   * lines and pushed the actions off the edge. The crumbs are not lost: the
   * last one that goes somewhere *is* the chevron, which is where a thumb
   * expects it and is a bigger target than the word was.
   */
  const back = [...(breadcrumbs ?? [])].reverse().find((crumb) => crumb.onClick)

  if (phone) {
    return (
      <div className={cn('flex flex-col border-b border-line bg-surface px-4 pb-2.5 pt-1.5',
        hasTabs && 'pb-0')}>
        <div className="flex items-center gap-2.5">
          {back && (
            <button
              type="button"
              onClick={back.onClick}
              aria-label={`Back to ${back.label}`}
              className="-ml-1 flex shrink-0 p-1 text-text"
            >
              <IconChevronLeft size={20} />
            </button>
          )}

          <span className="flex min-w-0 flex-col gap-px">
            {title && (
              <h1 className="m-0 truncate text-[17px] font-bold -tracking-[0.01em] text-text">
                {title}
              </h1>
            )}
            {sub && <span className="truncate text-[12px] text-text-muted">{sub}</span>}
          </span>

          {status}
          {actions && <div className="ml-auto flex shrink-0 gap-2">{actions}</div>}
        </div>

        {hasTabs && tabRow}
      </div>
    )
  }

  return (
    <div className={cn(
      'flex flex-col gap-2 border-b border-line bg-surface px-4 pt-2.5',
      !hasTabs && 'pb-2.5',
    )}>
      <div className="flex items-center gap-2">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[12px]">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.label} className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={crumb.onClick}
                  className="text-text-muted hover:text-text"
                >
                  {crumb.label}
                </button>
                {/*
                  * Between the crumbs, and after the last one only when a
                  * title follows it — "Properties / Riverfront Office Park",
                  * "Settings / Catalog" (mockups `header-page-bar.html`,
                  * `packages-settings-home.html`). Drawing it unconditionally
                  * left every record page reading "Contacts / Grace Okonkwo /";
                  * never drawing it ran the last crumb into the title.
                  */}
                {(index < breadcrumbs.length - 1 || Boolean(title)) && (
                  <span aria-hidden className="text-line-strong">/</span>
                )}
              </span>
            ))}
          </nav>
        )}

        {/* An empty h1 still takes a line's height, which is the other half
            of why a breadcrumb-only bar read as top-aligned. */}
        {title && (
          <h1 className={cn('m-0 text-text', variant === 'page'
            ? 'text-[18px] font-bold -tracking-[0.01em]'
            : 'text-title')}
          >
            {title}
          </h1>
        )}
        {sub && (
          <span className={cn('text-[12px]',
            variant === 'page' ? 'text-text-faint' : 'text-text-muted')}
          >
            {sub}
          </span>
        )}
        {status}

        {/* Phone collapses these into one "⋯" button (plan §3.4). */}
        {actions && <div className="ml-auto flex gap-2">{actions}</div>}
      </div>

      {tabRow}

      {menu && (
        <ContextMenu items={menu.items} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />
      )}
    </div>
  )
}
